import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clapperboard, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1594394489098-74ac04c0fc2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHx2aWRlbyUyMHByb2R1Y3Rpb24lMjBzdHVkaW8lMjBjYW1lcmElMjBnZWFyfGVufDB8fHx8MTc5MDcwNjY4MXww&ixlib=rb-4.1.0&q=85"
          alt="Studio"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <Clapperboard className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-display font-bold text-2xl">BellyBuddies Ops Center</span>
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
            Your entire video operation, <span className="text-primary">in one command center.</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-sm">
            Tasks, video library, social metrics and release calendar — built for production teams.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Clapperboard className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display font-bold text-lg">BellyBuddies Ops Center</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in</h1>
          <p className="text-muted-foreground text-sm mt-2 mb-8">Access your ops workspace.</p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@studio.com" data-testid="login-email-input" autoComplete="email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2" data-testid="login-error-message">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} className="w-full font-semibold" data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
